import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { TypographyRhythm } from '@jouzen/ecom-components/v3/TypographyRhythm';
import { useTranslations } from 'next-intl';

import PageLayout from '@/app/components/PageLayout';

import { AccessAndUseSection } from '../_components/AccessAndUseSection';
import CurrentTermsAndConditionsLink from '../_components/CurrentTermsAndConditionsLink';
import { TermsOfDeliverySection } from '../_components/TermsOfDeliverySection';

const Page = () => {
  const t = useTranslations();

  return (
    <PageLayout
      pageName="Terms and Conditions"
      containerProps={{
        name: 'terms-and-conditions',
        padding: 'both',
        lang: 'es',
      }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="terms-and-conditions-es-page">
        <TypographyRhythm className="col-main">
          <Typography Element="h1" variant="h1">
            CONDICIONES DE USO DE ŌURA
          </Typography>
          <CurrentTermsAndConditionsLink />
          <Typography>
            <em>Última actualización: 2 de abril de 2024</em>
          </Typography>
          <Typography>
            ¡Bienvenido y gracias por mostrar interés en Ōura! En estas
            Condiciones de uso (el "Acuerdo") se describen los términos y las
            condiciones aplicables al uso de ouraring.com (el "Sitio"), el
            anillo inteligente, el cargador y otros dispositivos electrónicos
            (los "Productos"), incluida la aplicación móvil de Ōura (la
            "Aplicación móvil") (en su conjunto, los "Servicios"). El Sitio y la
            Aplicación móvil son propiedad y están operados por{' '}
            <strong>Ōura Health Oy</strong>, sus filiales y sus subsidiarias,
            como Ouraring Inc. (colectivamente "Ōura" u "Oura").
          </Typography>
          <Typography>
            En este Acuerdo, nos referimos a nosotros mismos como "Ōura", "Oura"
            o "nosotros", y nos referimos al usuario como "Usuario" o "Cliente".
            Se utiliza "Parte" para hacer referencia a Oura y al Cliente de
            forma individual, y "Partes", para hacerlo de forma colectiva.
          </Typography>
          <Typography>
            Al acceder a los Servicios o utilizarlos, lo que también incluye el
            acceso al Sitio, el Usuario pretende y acepta expresamente estar
            sujeto a todos los términos y condiciones del presente Acuerdo y a
            la Política de Privacidad (disponible en{' '}
            <BodyLink
              color="inherit"
              href="https://ouraring.com/privacy-policy-oura-health/es"
              target="_blank"
            >
              https://ouraring.com/privacy-policy-oura-health/es
            </BodyLink>
            ), incorporada como referencia. Si no acepta estos términos y
            condiciones, no podrá utilizar los Servicios. como «usuario» o «su».
          </Typography>

          <AccessAndUseSection />

          <Typography Element="h2" variant="h3">
            Responsabilidades del Cliente
          </Typography>
          <Typography>
            El Usuario reconoce que es el único responsable del uso que haga de
            los Servicios, directa o indirectamente, lo cual incluye saber si
            dicho acceso o uso está o no permitido en virtud del presente
            Acuerdo, y es el único responsable del cumplimiento de todas las
            leyes aplicables relacionadas con el uso de los Servicios. Asimismo,
            el Usuario utilizará los Servicios únicamente para fines legales y
            realizará todas las actividades comerciales a través de los
            Servicios de conformidad con todas las leyes y normativas
            aplicables, incluidas, entre otras, todas las leyes y normativas
            federales y estatales aplicables que rijan la oferta y venta de
            valores, el blanqueo de dinero y la lucha contra el terrorismo.
          </Typography>
          <Typography>
            El Usuario es el único responsable de garantizar y mantener la
            capacidad de acceso y uso de los Servicios, así como de proteger su
            propio hardware compatible, la Aplicación móvil, el acceso a
            Internet, cualquier aplicación móvil de seguridad, los dispositivos
            o servicios de copia de seguridad y cualquier otro requisito. Oura
            no es responsable de proporcionar ninguna aplicación móvil o
            hardware adicional. Además, el Usuario acepta que Oura no tendrá
            ninguna responsabilidad por cualquier pérdida de datos u otros daños
            o pérdidas sufridas en relación con el uso de los Servicios,
            incluido cualquier fallo en la provisión de dispositivos o servicios
            de seguridad adecuados.
          </Typography>
          <Typography>
            El Usuario es responsable de asegurarse de que Oura tenga
            información precisa y actualizada de su cuenta de Cliente, incluida
            la información de contacto y de pago actual. Además, es responsable
            de revisar con regularidad la cuenta de correo electrónico asociada
            de Cliente para ver si ha recibido alguna comunicación de Oura.
          </Typography>
          <Typography>
            Si el Usuario recibe un nombre de usuario, una contraseña, un
            archivo de credenciales o cualquier otra información como parte de
            cualquier procedimiento de seguridad ("Credenciales"), deberá tratar
            dicha información como confidencial y no podrá desvelarla a ninguna
            otra persona o entidad. El Usuario reconoce que su cuenta y sus
            credenciales son personales y se compromete a no facilitar a ninguna
            otra persona el acceso a los Servicios o a alguna parte de los
            mismos utilizando su nombre de usuario, contraseña u otros datos
            relativos a la seguridad. Oura tiene derecho a desactivar cualquier
            nombre de usuario, contraseña, archivo de credenciales u otro
            identificador en cualquier momento, ya sea elegido por el Usuario o
            proporcionado por Oura.
          </Typography>
          <Typography>
            Oura hará esfuerzos comercialmente razonables para proporcionar una
            asistencia adecuada para los Servicios. Sin perjuicio de lo
            anterior, el presente Acuerdo no confiere el derecho a ningún nivel,
            disponibilidad o tiempo de respuesta garantizados de asistencia
            técnica para los Servicios.
          </Typography>
          <Typography Element="h2" variant="h3">
            Pagos y tasas
          </Typography>
          <Typography>
            Los Servicios de pago incluyen los Servicios, que pueden ser compras
            únicas o servicios de suscripción de renovación automática
            ("Servicios de pago"), incluido nuestro Sitio y Aplicación móvil
            ("Suscripciones"). Oura podrá realizar cambios en los Servicios de
            pago, así como suspenderlos o interrumpirlos en cualquier momento y
            por cualquier motivo, y se reserva la facultad de determinar qué
            Servicios o partes de los mismos requieren pago.
          </Typography>
          <Typography>
            Los Servicios de pago pueden incluir productos solicitados por
            adelantado que se generarán para el Usuario en el futuro ("Pedido
            anticipado"). Al realizar el Pedido anticipado, se le cobrará la
            tasa de Pedido anticipado correspondiente. La fecha real de envío de
            cualquier Pedido anticipado aceptado dependerá de una serie de
            factores, incluyendo, entre otros, la fecha de pago de la tasa del
            Pedido anticipado y el calendario de fabricación de Oura. No puede
            garantizarse la fecha de envío exacta de los Pedidos anticipados.
          </Typography>
          <Typography>
            El Usuario se compromete a pagar todas las tasas aplicables a los
            Servicios de pago, incluyendo, sin exclusión, las tasas de
            suscripción mensual, las tasas de usuario y las tasas de oferta, así
            como cualquier otra tasa, cargo o coste que acepte adquirir como
            parte de los Servicios de pago durante el proceso de compra
            ("Tasas"). El Usuario se compromete a pagar todas las Tasas y todos
            los impuestos aplicables en los que se incurra antes de la rescisión
            o cancelación del Acuerdo.
          </Typography>
          <Typography>
            El Usuario autoriza a Oura a efectuar el cargo de los Servicios de
            Pago a través del método de pago que haya designado. Al proporcionar
            un método de pago aceptable, el Usuario declara y garantiza que está
            autorizado a utilizar el método de pago designado y que nos autoriza
            a nosotros o a nuestro procesador de pagos a cargar el importe total
            de la compra, incluidos los impuestos y otros cargos aplicables, a
            su método de pago. Si el método de pago no puede verificarse, no es
            válido o no es aceptable por cualquier otro motivo, el Servicio de
            Pago podrá ser suspendido o cancelado. Para poder continuar con el
            uso del Servicio, el Usuario deberá resolver cualquier problema que
            encontremos en relación con el método de pago facilitado. Si acepta
            una oferta promocional o realiza cambios en sus Servicios de Pago,
            las Cuotas, los impuestos y los importes facturados pueden variar.
            Los importes de facturación también pueden variar debido a cambios
            en los impuestos aplicables o en los tipos de cambio. El Usuario nos
            autoriza a nosotros o a nuestro procesador de pagos a cargar el
            importe correspondiente a su método de pago. No se efectuarán
            reembolsos a menos que lo exija la ley. Los descuentos no pueden
            combinarse ni acumularse. Esta obligación de pago perdurará tras la
            terminación o cancelación por cualquier motivo del presente Acuerdo.
          </Typography>
          <Typography>
            Si opta por financiar una compra a través de nuestro procesador de
            pagos de terceros y uno o más artículos de su pedido tiene una fecha
            de envío ampliada, los pagos de su préstamo, incluidos los
            intereses, pueden vencer antes de que enviemos todos los artículos.
            El Usuario debe tener en cuenta que no podrá recibir un reembolso de
            los intereses que ya se hayan acumulado sobre un importe que se
            reembolse posteriormente.
          </Typography>
          <Typography Element="h2" variant="h3">
            Suscripciones
          </Typography>
          <Typography>
            Algunos Servicios de pago son compras basadas en una suscripción, a
            los que se aplican las siguientes condiciones:
          </Typography>
          <Typography>
            <strong>
              El plazo de la suscripción puede variar en forma de plazo
              continuo, mensual o anual ("Periodos de suscripción"), como se
              describe en el transcurso de la compra de los Servicios de pago.
              La Suscripción se renovará automáticamente por períodos de
              suscripción adicionales hasta que el Usuario la cancele o hasta
              que Oura la suspenda o rescinda. A menos que indiquemos lo
              contrario, se efectuará un cargo al método de pago designado por
              el Usuario antes o al comienzo de cada Período de suscripción por
              la tasa de Suscripción más los impuestos y otros cargos
              aplicables. Antes de efectuar el cobro de un periodo de
              suscripción, se notificará la tasa aplicable, y la renovación se
              efectuará según el precio vigente en ese momento para el Servicio
              de pago.
            </strong>
          </Typography>
          <Typography>
            <strong>
              El Usuario podrá cancelar su Suscripción en cualquier momento. Su
              cancelación tendrá efecto al final del Período de suscripción en
              curso. Para cancelar su suscripción y el pago automático, deberá
              hacer clic en el botón "Cancel Account" (Cancelar cuenta) de la
              pantalla de su cuenta o bien enviar un correo electrónico a
              support@ouraring.com. La cancelación no otorga el derecho a la
              devolución de las tasas pagadas anteriormente y no se reembolsará
              el prorrateo del resto del Periodo de suscripción. En caso de que
              el Usuario cancele su Suscripción, deberá tener en cuenta que
              podremos seguir enviándole comunicaciones promocionales, a menos
              que opte por dejar de recibirlas siguiendo las instrucciones de
              cancelación de la suscripción que se incluyen en las
              comunicaciones.
            </strong>
          </Typography>
          <Typography>
            <strong>
              Al cancelar una Suscripción, solo se cancelan los cargos futuros
              de la misma. El Usuario no recibirá ningún reembolso por el
              Período de suscripción en curso que haya pagado, pero seguirá
              teniendo pleno acceso a esa Suscripción hasta el final del Período
              de suscripción en curso
            </strong>
            . En cualquier momento y por cualquier motivo, podemos ofrecer un
            reembolso, un descuento u otra contraprestación ("Créditos") a
            algunos de nuestros usuarios o a todos ellos. El importe y la forma
            de dichos Créditos, así como la decisión de ofrecerlos, quedan a
            nuestra entera y absoluta discreción. La concesión de Créditos en un
            caso no da derecho a Créditos en el futuro para casos similares, ni
            nos obliga a conceder Créditos en el futuro.
          </Typography>
          <Typography>
            Si el Usuario reside fuera de los Estados Unidos y cambia de opinión
            sobre su compra, puede tener derecho a recibir un reembolso completo
            en un plazo de catorce (14) días ("Periodo de reflexión"), siempre
            que no haya iniciado sesión, canjeado o comenzado a utilizar los
            Servicios como suscriptor durante el Periodo de reflexión.
          </Typography>
          <Typography>
            Ocasionalmente, podemos ofrecer pruebas gratuitas de ciertas
            Suscripciones durante períodos específicos de tiempo.{' '}
            <strong>
              Antes de comenzar el periodo de prueba gratuito, el Usuario
              recibirá una notificación sobre las tasas de suscripción
              aplicables que se cobrarán al finalizar el periodo de prueba
              gratuito. A menos que el Usuario cancele su Suscripción antes de
              que finalice el periodo de prueba gratuito siguiendo los pasos
              descritos anteriormente, cuando su prueba gratuita finalice,
              nosotros o nuestro procesador de pagos externo efectuaremos el
              cobro de la tasa de Suscripción al método de pago designado de
              forma periódica, más los impuestos aplicables y otros cargos,
              durante el tiempo que dure su Suscripción. El Usuario deberá
              cancelar su Suscripción antes de que finalice el periodo de prueba
              gratuito para evitar cualquier cargo. Las instrucciones para
              cancelar su Suscripción se describen más arriba.
            </strong>
          </Typography>
          <Typography>
            La información de pago se procesará y almacenará a través de un
            procesador de pagos externo. Todos los titulares de cuentas de pago
            deben mantener al menos un método de pago válido para el pago de las
            Tasas, descritas con más detalle durante el proceso de compra. Todas
            las Tasas se calculan y facturan con carácter mensual o anual, según
            la elección del Usuario, son pagaderas inmediatamente después de su
            recepción y están sujetas a cambios. El Usuario reconoce que las
            Tasas tienen una función de pago recurrente y acepta la
            responsabilidad de todos los cargos recurrentes antes de su
            cancelación. Las Tasas se cobrarán o cargarán en el método de pago
            guardado y designado que el Usuario indique un día antes del
            vencimiento mensual o anual de la fecha de compra inicial.
          </Typography>
          <Typography>
            En el caso de que el Usuario no haya iniciado sesión o no haya
            utilizado los Servicios durante seis (6) meses o más tras la compra
            o recepción de un Producto durante la prueba gratuita, nos
            reservamos el derecho a cancelar su Suscripción, anular cualquier
            compra pendiente y reembolsarle únicamente el precio de compra del
            Producto. No tendrá derecho a ningún reembolso por el valor de la
            Suscripción durante el periodo de prueba gratuito.
          </Typography>
          <Typography>
            Oura se reserva el derecho de ajustar en cualquier momento las Tasas
            de nuestros Servicios de pago, o cualquier característica o parte de
            nuestros Servicios de pago. El Usuario reconoce que Oura puede
            modificar las Tasas de los Servicios de pago en cualquier momento.
            En caso de que se produzca dicho cambio, Oura se lo notificará a
            través de la dirección de correo electrónico asociada a su cuenta
            con al menos treinta (30) días de antelación a la fecha efectiva del
            cambio. El uso continuado de los Servicios por parte del Usuario
            supone la aceptación de cualquier cambio en las Tasas. El Usuario es
            el único responsable de todos los impuestos aplicables, que se
            cobrarán cuando así lo exija la ley.
          </Typography>
          <Typography Element="h2" variant="h3">
            Datos y comunicaciones
          </Typography>
          <Typography>
            Oura puede recopilar y procesar información relativa al uso de los
            Servicios por parte del Usuario, y este acepta que Oura recopile y
            utilice dicha información, así como que la comparta con terceros
            proveedores de servicios con el fin de proporcionar, comercializar y
            mejorar los Servicios, y por cualquier otro motivo descrito en la
            Política de privacidad. Toda la información personal recopilada por
            Oura se trata de conformidad con la Política de privacidad.
          </Typography>
          <Typography>
            <strong>
              Al aceptar los términos y condiciones del presente Acuerdo y
              proporcionar su información de contacto a Oura, el Usuario da su
              consentimiento expreso para permitir que Oura, sus filiales y
              agentes se pongan en contacto con él ocasionalmente a través de
              cualquier dirección postal, número de teléfono o dirección de
              correo electrónico que proporcione a Oura
            </strong>
            . El consentimiento significa que el Usuario acepta que Oura y sus
            proveedores de servicios se pongan en contacto con él por teléfono,
            correo electrónico, mensajes de texto u otros medios para cualquier
            fin, incluyendo, entre otros, las notificaciones relacionadas con
            los Servicios y su cuenta, las suscripciones, las compras, las
            actualizaciones disponibles, los problemas de facturación y
            procesamiento de pagos y las comunicaciones de telemarketing. Estas
            comunicaciones autorizadas pueden incluir el uso de tecnología de
            marcación automática o el uso de mensajes previamente grabados. El
            Usuario es responsable de los gastos que le puedan facturar sus
            proveedores de servicios cuando nos pongamos en contacto con él.
            Además, reconoce que su consentimiento para lo anterior no es una
            condición para utilizar los Servicios de Oura y, si no desea dar su
            consentimiento, puede ponerse en contacto con nosotros y solicitar
            que se le incluya en una lista de personas que no desean ser
            contactadas, o puede excluirse en cualquier momento utilizando el
            mecanismo de exclusión previsto en dichas comunicaciones.
          </Typography>
          <Typography>
            Oura se exime de toda responsabilidad en virtud de este Acuerdo por
            cualquier información que el Usuario proporcione a Oura y que pueda
            constituir una historia clínica electrónica de paciente o
            información similar suministrada por el Usuario o por un usuario
            final, sin perjuicio de cualquier disposición contraria contenida en
            el presente Acuerdo o de cualquier otra exigencia derivada de las
            leyes, normas o reglamentos federales, estatales o internacionales
            aplicables.
          </Typography>
          <Typography Element="h2" variant="h3">
            Derechos de propiedad intelectual
          </Typography>
          <Typography>
            ŌURA, Ō y OURA son marcas registradas de Ōura Health Oy. El
            contenido de Oura, los productos de Oura, las funciones y los
            servicios de Oura y nuestra tecnología subyacente están protegidos
            por derechos de autor, marca registrada, patente, propiedad
            intelectual y otras leyes de los Estados Unidos y otros países.
            Todos los derechos quedan reservados. No se concede al Usuario, por
            implicación ni de otro modo, ninguna licencia o derecho de uso de
            ninguna marca que aparezca en los Servicios, o que se utilice o
            muestre en relación con ellos ("Marcas registradas"). Los Servicios
            también pueden contener o hacer referencia a marcas registradas,
            nombres comerciales, nombres de productos y logotipos de terceros
            que pueden ser marcas registradas de sus respectivos propietarios.
            El Usuario no podrá, bajo ninguna circunstancia, utilizar o copiar
            ninguna de las Marcas registradas. Nada de lo aquí expuesto debe
            interpretarse como la concesión de una licencia o derecho de uso de
            las Marcas registradas expuestas en relación con los Servicios sin
            la autorización expresa por escrito de Oura.
          </Typography>
          <Typography>
            Todo el contenido proporcionado en relación con los Servicios y el
            presente Acuerdo, incluidos, a título informativo y no limitativo,
            el Sitio, el Producto, la Aplicación móvil, todo el texto, los
            gráficos, las interfaces de usuario, las interfaces visuales, las
            fotografías, las imágenes/vídeos, el material gráfico electrónico,
            los sonidos/audios, los datos, los programas de comunicación, el
            código ejecutable, el código informático y los datos
            (colectivamente, el "Contenido") formateados, organizados y
            recopilados en diferentes formatos, incluidos el diseño, la
            estructura, la selección, la coordinación, la expresión, el aspecto,
            la disposición, los diseños, las páginas, las pantallas y las bases
            de datos de dicho Contenido incluidos en el Contenido, los Servicios
            y la tecnología subyacente, así como cualquier otra obra protegida
            por derechos de autor asociada a los Servicios ("Obras protegidas
            por derechos de autor"), son de propiedad exclusiva de Oura o están
            controlados por ella o concedidos bajo licencia, y están protegidos
            por las leyes de derechos de autor estadounidenses e
            internacionales. El Usuario acepta que no copiará, reproducirá,
            modificará, creará obras derivadas, distribuirá o mostrará
            públicamente las Obras protegidas por derechos de autor sin la
            previa autorización expresa por escrito de Oura.
          </Typography>
          <Typography>
            Si el Usuario proporciona cualquier comunicación o material a Oura
            por correo postal, correo electrónico, teléfono o por otros medios,
            sugiriendo o recomendando cambios en los Servicios, incluyendo, a
            título informativo y no limitativo, nuevas características o
            funcionalidades relacionadas con los mismos, o cualquier comentario,
            pregunta, sugerencia o similar ("Feedback"), Oura es libre de
            utilizar dicho Feedback independientemente de cualquier otra
            obligación o limitación entre las Partes que rija dicho Feedback.
            Oura es libre de utilizar, sin atribución o compensación a ninguna
            parte, cualquier idea, conocimiento, concepto, técnica u otros
            derechos de propiedad intelectual contenidos en el Feedback, para
            cualquier propósito, aunque Oura no está obligada a utilizar ningún
            Feedback.
          </Typography>
          <Typography>
            Oura respeta los derechos de propiedad intelectual de otros y
            nuestra política es procesar y revisar rápidamente las
            notificaciones de reclamaciones de infracción de derechos de autor u
            otras leyes de propiedad intelectual aplicables. Cualquier
            notificación de infracción deberá ser enviada al Representante
            designado de Oura en ip@ouraring.com, y deberá contener todo lo
            siguiente: (i) una firma (física o electrónica) del propietario de
            los derechos de autor o de una persona autorizada para actuar en
            nombre del propietario de los derechos de autor; (ii) una
            descripción del trabajo protegido por derechos de autor que el
            Usuario afirma que se ha vulnerado; (iii) una descripción del
            material que el Usuario afirma que ha vulnerado los derechos de
            autor y que debe ser eliminado o cuyo acceso debe ser deshabilitado,
            e información suficiente para permitir a los administradores de Oura
            localizar el material; (iv) información suficiente para que podamos
            ponernos en contacto con el Usuario, como la dirección, el número de
            teléfono y la dirección de correo electrónico; (v) una declaración
            en la que manifieste que cree de buena fe que el uso del material en
            la forma denunciada no está autorizado por el propietario de los
            derechos de autor, su representante o la ley; y (vi) una declaración
            de que la información contenida en la notificación es exacta y, bajo
            pena de perjurio, que el Usuario es el propietario de los derechos
            de autor o está autorizado a actuar en nombre del propietario de los
            derechos de autor presuntamente vulnerados.
          </Typography>
          <Typography Element="h2" variant="h3">
            Descargo de responsabilidad de la garantía
          </Typography>
          <Typography>
            LOS SERVICIOS SE LE PROPORCIONAN "TAL CUAL, CON TODOS LOS FALLOS" Y
            "TAL COMO ESTÁN DISPONIBLES", SIN GARANTÍA DE NINGÚN TIPO, Y OURA Y
            SUS SUBSIDIARIAS, AFILIADOS, DIRECTIVOS, EMPLEADOS, REPRESENTANTES,
            AGENTES, SOCIOS Y OTORGANTES DE LICENCIAS RENUNCIAN POR EL PRESENTE
            DOCUMENTO A TODAS LAS GARANTÍAS Y CONDICIONES CON RESPECTO A LOS
            SERVICIOS, YA SEAN EXPRESAS O IMPLÍCITAS, Y RENUNCIAN EXPRESAMENTE A
            CUALQUIER GARANTÍA IMPLÍCITA DE TÍTULO, COMERCIABILIDAD, IDONEIDAD
            PARA UN FIN DETERMINADO, CALIDAD SATISFACTORIA Y NO INFRACCIÓN. OURA
            NO GARANTIZA QUE NO SE PRODUZCAN INTERFERENCIAS EN EL DISFRUTE DE
            LOS SERVICIOS, LA DISPONIBILIDAD DE LOS CONTENIDOS, QUE LAS
            FUNCIONES CONTENIDAS EN LOS SERVICIOS SATISFAGAN SUS NECESIDADES,
            QUE LOS SERVICIOS ESTÉN LIBRES DE VIRUS U OTROS COMPONENTES DAÑINOS,
            QUE EL FUNCIONAMIENTO DE LOS SERVICIOS SEA ININTERRUMPIDO O ESTÉ
            LIBRE DE ERRORES, QUE SE CORRIJAN LOS DEFECTOS DE LOS SERVICIOS, O
            QUE LAS FUNCIONES CONTENIDAS EN LOS SERVICIOS FUNCIONEN CON OTRAS
            APLICACIONES MÓVILES O HARDWARE, O DENTRO DE UN SISTEMA. NINGUNA
            INFORMACIÓN O CONSEJO VERBAL O POR ESCRITO DADO POR OURA O UN
            REPRESENTANTE AUTORIZADO DE OURA SUPONDRÁ UNA GARANTÍA. ALGUNAS
            JURISDICCIONES NO PERMITEN LA EXCLUSIÓN DE LAS GARANTÍAS IMPLÍCITAS
            NI LA LIMITACIÓN DE LOS DERECHOS LEGALES APLICABLES A UN CONSUMIDOR,
            POR LO QUE LA EXCLUSIÓN ANTERIOR PUEDE NO SER APLICABLE EN SU CASO.
          </Typography>
          <Typography>
            SIN PERJUICIO DE LO ANTERIOR, OURA GARANTIZA AL USUARIO FINAL Y
            COMPRADOR ORIGINAL QUE EL PRODUCTO ESTÁ LIBRE DE DEFECTOS DE
            MATERIAL Y MANO DE OBRA DURANTE UN (1) AÑO A PARTIR DE LA FECHA DE
            COMPRA. Si la legislación local vigente en el momento de la compra
            exige un período de garantía superior a un (1) año, esta garantía se
            ampliará en la medida en que lo exija dicha legislación. Dentro del
            periodo de garantía, Oura reparará o sustituirá sin coste alguno
            para el Usuario cualquier componente del Producto que no cumpla con
            la garantía limitada proporcionada. El Usuario se hará cargo de los
            gastos de envío correspondientes. Los productos de sustitución
            pueden ser nuevos o reacondicionados a nuestra discreción. Esta
            garantía limitada no se aplica a (i) el desgaste normal, incluidos
            los arañazos y las abolladuras; (ii) las piezas consumibles
            incluidas en el Producto, como las baterías, a menos que el daño del
            producto se haya producido debido a un defecto en los materiales o
            en la mano de obra; (iii) los daños resultantes de no haber
            utilizado el Producto de acuerdo con las instrucciones que acompañan
            al Producto de Ōura o que están disponibles en el sitio web; (iv)
            los daños resultantes de un accidente, una inundación, un incendio,
            un mal uso o un abuso; (v) los daños resultantes del servicio
            realizado o de la manipulación o las alteraciones del Producto, por
            cualquier persona no autorizada por Ōura; o (vi) el uso del Producto
            con cualquier aplicación o software que no sea la Aplicación móvil.
          </Typography>
          <Typography>
            Oura se reserva el derecho exclusivo de reparar o reemplazar el
            Producto, u ofrecer un reembolso completo, a su sola discreción.
            Dicho remedio será su único y exclusivo remedio para cualquier
            incumplimiento de la presente garantía limitada. Las reparaciones y
            sustituciones en garantía tienen una nueva garantía que es de
            noventa (90) días o lo que reste de la garantía original de un (1)
            año, lo que sea más largo.
          </Typography>
          <Typography Element="h2" variant="h3">
            Limitación de la responsabilidad
          </Typography>
          <Typography>
            EN NINGÚN CASO OURA, SUS SUBSIDIARIAS, AFILIADOS, DIRECTIVOS,
            EMPLEADOS, REPRESENTANTES, AGENTES, SOCIOS Y LICENCIATARIOS SERÁN
            RESPONSABLES DE DAÑOS DE NINGÚN TIPO, BAJO NINGUNA TEORÍA LEGAL, QUE
            SE DERIVEN DE O ESTÉN RELACIONADOS CON EL USO, O LA IMPOSIBILIDAD DE
            USAR, LOS SERVICIOS O CUALQUIER CONTENIDO ASOCIADO CON LOS
            SERVICIOS, O CUALQUIER OTRO SITIO O CUALQUIER SERVICIO O ARTÍCULO
            OBTENIDO A TRAVÉS DE LOS SERVICIOS, INCLUYENDO CUALQUIER DAÑO
            DIRECTO, INDIRECTO, DAÑOS ESPECIALES, INCIDENTALES, CONSECUENTES O
            PUNITIVOS, INCLUIDOS, ENTRE OTROS, LOS DAÑOS PERSONALES, EL DOLOR Y
            EL SUFRIMIENTO, EL ESTRÉS EMOCIONAL, LA PÉRDIDA DE INGRESOS, LA
            PÉRDIDA DE BENEFICIOS, LA PÉRDIDA DE NEGOCIO O DE AHORROS PREVISTOS,
            LA PÉRDIDA DE OPORTUNIDADES DE NEGOCIO, LA INTERRUPCIÓN DE LA
            ACTIVIDAD COMERCIAL, LA PÉRDIDA DE USO, LA PÉRDIDA DE FONDO DE
            COMERCIO, LA PÉRDIDA DE DATOS, TANTO SI SON CAUSADOS POR AGRAVIO
            (INCLUIDA LA NEGLIGENCIA) COMO POR INCUMPLIMIENTO DE CONTRATO O DE
            OTRO MODO, INCLUSO SI SON PREVISIBLES. ESTAS EXCLUSIONES O
            LIMITACIONES SE APLICARÁN INDEPENDIENTEMENTE DE QUE SE HAYA
            ADVERTIDO A OURA DE LA POSIBILIDAD DE TALES DAÑOS.
          </Typography>
          <Typography>
            LO ANTERIOR NO AFECTA A NINGUNA RESPONSABILIDAD QUE NO PUEDA
            EXCLUIRSE O LIMITARSE EN VIRTUD DE LA LEGISLACIÓN APLICABLE. EN LA
            MEDIDA EN QUE LA RESPONSABILIDAD NO PUEDA EXCLUIRSE O LIMITARSE
            SEGÚN LO ESTABLECIDO ANTERIORMENTE, OURA NO SERÁ RESPONSABLE EN
            NINGÚN CASO DE NINGUNA RECLAMACIÓN, YA SEA CONTRACTUAL,
            EXTRACONTRACTUAL O BAJO CUALQUIER OTRA TEORÍA DE RESPONSABILIDAD,
            QUE SUPERE LOS 100 EUROS.
          </Typography>
          <Typography Element="h2" variant="h3">
            Precauciones
          </Typography>
          <Typography>
            LOS SERVICIOS NO CONSTITUYEN UN DISPOSITIVO MÉDICO Y EL USUARIO
            ACEPTA EXPRESAMENTE QUE NO IMPLICAN LA PRESTACIÓN DE ASESORAMIENTO
            MÉDICO POR PARTE DE OURA. LOS SERVICIOS NO ESTÁN DESTINADOS A
            DIAGNOSTICAR, TRATAR, CURAR O PREVENIR NINGUNA ENFERMEDAD O
            CONDICIÓN MÉDICA. LA FINALIDAD DE LOS SERVICIOS ES MERAMENTE
            INFORMATIVA Y NO PUEDEN SUSTITUIR A LOS SERVICIOS DE MÉDICOS O
            PROFESIONALES DE LA SALUD.
          </Typography>
          <Typography>
            LOS SERVICIOS, INCLUIDA TODA LA INFORMACIÓN, TEXTO, FOTOGRAFÍAS,
            IMÁGENES, ILUSTRACIONES, GRÁFICOS, AUDIO, VÍDEO Y CLIPS DE
            AUDIO-VÍDEO, Y OTROS MATERIALES, YA SEAN PROPORCIONADOS POR NOSOTROS
            O POR TERCEROS, NO ESTÁN DESTINADOS A SER Y NO DEBEN SER UTILIZADOS
            EN LUGAR DE (A) EL CONSEJO DEL MÉDICO U OTROS PROFESIONALES DE LA
            SALUD, (B) UNA VISITA, LLAMADA O CONSULTA CON EL MÉDICO U OTROS
            PROFESIONALES DE LA SALUD, O (C) LA INFORMACIÓN CONTENIDA EN EL
            EMBALAJE O LA ETIQUETA DEL PRODUCTO.
          </Typography>
          <Typography>
            SI TIENE ALGUNA PREGUNTA RELACIONADA CON LA SALUD, LLAME O ACUDA A
            SU MÉDICO U OTRO PROVEEDOR DE SERVICIOS MÉDICOS CON PRONTITUD. EN
            CASO DE EMERGENCIA, LLAME A SU MÉDICO O AL 112 INMEDIATAMENTE. NUNCA
            DEBE IGNORAR EL CONSEJO MÉDICO O RETRASAR LA BÚSQUEDA DE CONSEJO
            MÉDICO DEBIDO A CUALQUIER INFORMACIÓN PRESENTADA EN LOS SERVICIOS, Y
            NO DEBE UTILIZAR LOS SERVICIOS O CUALQUIER INFORMACIÓN PROPORCIONADA
            EN ELLOS PARA DIAGNOSTICAR O TRATAR UN PROBLEMA DE SALUD. LA
            TRANSMISIÓN Y RECEPCIÓN DE SERVICIOS, EN SU TOTALIDAD O EN PARTE, O
            LA COMUNICACIÓN A TRAVÉS DE INTERNET, CORREO ELECTRÓNICO U OTROS
            MEDIOS NO CONSTITUYE NI CREA UNA RELACIÓN MÉDICO-PACIENTE,
            TERAPEUTA-PACIENTE U OTRA RELACIÓN PROFESIONAL SANITARIA ENTRE EL
            USUARIO Y OURA.
          </Typography>
          <Typography>
            El Usuario siempre debe consultar a un médico antes de realizar
            cualquier cambio en su sueño o actividad sobre la base de la
            información proporcionada a través de los Servicios, o si tiene
            alguna pregunta relacionada con una condición médica. Oura no se
            hace responsable de los problemas de salud que puedan derivarse de
            la información que el Usuario obtenga a través de los Servicios. Si
            el Usuario realiza algún cambio en su sueño o actividad basándose en
            los Servicios, acepta que lo hace bajo su propia responsabilidad. Es
            importante prestar atención a las respuestas del cuerpo. Por
            ejemplo, si siente un dolor inesperado, repetitivo o de larga
            duración, o fatiga o malestar por haber realizado cambios en su
            sueño o actividad, se recomienda consultar a un médico antes de
            continuar con dichos cambios. La información de los Servicios puede
            ser engañosa si sus funciones y respuestas fisiológicas difieren
            significativamente de los promedios de la población debido a
            condiciones médicas o diferencias naturales inusuales.
          </Typography>
          <Typography>
            Procure que el Producto que lleva puesto no se enganche en
            estructuras fijas u objetos pesados. Si experimenta enrojecimiento o
            irritación de la piel en el dedo mientras utiliza el Producto,
            quíteselo inmediatamente. Si los síntomas persisten durante más de 2
            o 3 días sin usar el Producto, póngase en contacto con un
            profesional médico.
          </Typography>
          <Typography>
            Nuestro Producto no debe introducirse en la boca en ningún momento.
            El Producto de Oura no es un juguete ni está destinado a ser
            utilizado por niños. No se debe dejar a los niños sin supervisión
            con este Producto, ya que puede suponer un peligro de asfixia.
          </Typography>
          <Typography>
            Los Servicios pueden proporcionar enlaces a otros sitios web
            mantenidos por terceros. El Usuario reconoce y acepta que dichos
            enlaces se proporcionan únicamente para su comodidad y no reflejan
            ningún tipo de respaldo, afiliación, relación o patrocinio por parte
            de Oura con respecto al proveedor de dicho sitio vinculado o a la
            calidad, fiabilidad o cualquier otra característica o función de
            dicho sitio vinculado. Además, el Usuario reconoce y acepta que Oura
            no es responsable de ninguna manera (ni siquiera con respecto a
            cualquier pérdida o lesión que pueda sufrir) de cualquier asunto
            relacionado con el sitio vinculado, incluyendo, a título informativo
            y no limitativo, el contenido proporcionado en o a través de
            cualquier sitio vinculado o su confianza en el mismo. Además, debe
            tener en cuenta que el uso de cualquier sitio de terceros está
            sujeto a los términos y condiciones aplicables a ese sitio,
            incluidas las políticas de privacidad (o la ausencia de ellas) del
            mismo. El hecho de que un tercero enlace a los Servicios no es
            necesariamente una indicación de respaldo, afiliación, relación o
            patrocinio por parte de o con Oura. Oura puede incluso no ser
            consciente de que un tercero ha enlazado con los Servicios.
          </Typography>
          <Typography>
            Cualquier otro contenido que no sea propiedad de Oura es propiedad
            de su respectivo dueño. El Usuario reconoce y acepta que dicho
            contenido lo proporciona el propietario y no refleja ningún tipo de
            respaldo, afiliación, relación o patrocinio por parte de Oura con
            respecto al proveedor de dicho contenido. Asimismo, el Usuario
            reconoce y acepta que Oura no es responsable de ninguna manera (ni
            siquiera con respecto a cualquier pérdida o lesión que pueda sufrir)
            de cualquier contenido proporcionado por terceros, incluyendo, a
            título informativo y no limitativo, su confianza en el mismo. OURA
            NO HACE REPRESENTACIONES O GARANTÍAS CON RESPECTO A NINGÚN CONTENIDO
            DE TERCEROS.
          </Typography>
          <Typography>
            El Usuario se compromete a indemnizar, eximir de responsabilidad y
            defender a Oura, sus subsidiarias, afiliados, directivos, empleados,
            representantes, agentes, socios, licenciatarios, sucesores y
            cesionarios, de y contra cualquier acción, causa, reclamación, daño,
            deuda, demanda o responsabilidad, incluidos los costes razonables y
            los honorarios de los abogados, que se hagan valer por cualquier
            persona, que surjan de o estén relacionados con (i) el uso que el
            Usuario haga de los Servicios, incluyendo, a título informativo y no
            limitativo, cualquier persona que utilice su cuenta o sus
            Credenciales; (ii) el incumplimiento del presente Acuerdo por parte
            del Usuario o de cualquier persona que utilice su cuenta o sus
            Credenciales; (iii) cualquier información utilizada, almacenada o
            transmitida en relación con su cuenta o sus Credenciales; (iv) la
            vulneración de los derechos de terceros, incluidos, entre otros, los
            derechos de privacidad, publicidad, propiedad intelectual u otros
            derechos de propiedad por su parte o por parte de cualquier persona
            que utilice su cuenta o sus Credenciales; o (v) la vulneración de
            cualquier ley, reglamento u otro requisito legal.
          </Typography>
          <Typography Element="h2" variant="h3">
            Aviso para los usuarios de California
          </Typography>
          <Typography>
            De acuerdo con el artículo 1789.3 del Código Civil de California,
            los usuarios de los Servicios que residen en California tienen
            derecho a la siguiente notificación específica de derechos del
            consumidor:
          </Typography>
          <Typography>
            Pueden ponerse en contacto con la Unidad de Asistencia para
            Reclamaciones de la División de Servicios al Consumidor del
            Departamento de Asuntos del Consumidor de California por escrito a
            la dirección 1625 N. Market Blvd., Suite N 112, Sacramento,
            California 95834, o por teléfono al número (800) 952-5210.
          </Typography>
          <Typography Element="h2" variant="h3">
            Rescisión y cancelación
          </Typography>
          <Typography>
            El presente Acuerdo continuará en pleno vigor hasta que sea
            rescindido o cancelado con arreglo al mismo.
          </Typography>
          <Typography>
            Oura tendrá derecho a rescindir el presente Acuerdo (i) por
            cualquier motivo previa notificación al Usuario con treinta (30)
            días de antelación; (ii) inmediatamente por el incumplimiento
            material del presente Acuerdo por parte del Usuario, que no sea el
            impago de las Tasas; o (iii) por el impago de las Tasas. Sin
            perjuicio de lo anterior, Oura se reserva el derecho, a su entera
            discreción y sin previo aviso, en cualquier momento y por cualquier
            motivo, de eliminar, modificar, suspender o deshabilitar el acceso a
            todos o a cualquier parte de los Servicios.
          </Typography>
          <Typography>
            El Usuario podrá rescindir el Acuerdo por cualquier motivo
            notificándolo a Oura con treinta (30) días de antelación por correo
            electrónico a support@ouraring.com. El Usuario será responsable de
            todas las Tasas incurridas antes y durante el periodo de
            notificación.
          </Typography>
          <Typography>
            Las secciones tituladas Precauciones, Derechos de Propiedad
            Intelectual, Datos y comunicaciones, Indemnización, Renuncia a la
            garantía, Limitación de la responsabilidad, Legislación aplicable,
            Foro; Arbitraje obligatorio vinculante; Renuncia a la demanda
            colectiva, y las obligaciones de pago de las Tasas incurridas antes
            y durante cualquier período de notificación seguirán vigentes tras
            la rescisión del presente Acuerdo por cualquier motivo.
          </Typography>
          <Typography Element="h2" variant="h3">
            Restricciones de uso final del Gobierno Federal
          </Typography>
          <Typography>
            Si el Usuario es un departamento o una agencia del Gobierno Federal
            de Estados Unidos o contrata en nombre de dicho departamento o
            agencia, los Servicios son Productos Comerciales, tal y como se
            define ese término en el artículo 48 C.F.R. §2.101, y consisten en
            Software Comercial y Documentación sobre Software Comercial, tal y
            como se utilizan esos términos en los artículos 48 C.F.R. §12.212 y
            48 C.F.R. §227.7202. De acuerdo con el artículo 48 C.F.R. §12.212 o
            con los artículos que van del 48 C.F.R. §227.7202-1 al 48 C.F.R.
            §227.7202-4, según corresponda, el Servicio se concede bajo licencia
            solo con los derechos previstos en los términos y condiciones del
            presente Acuerdo. Si el Usuario es una entidad contratante del
            Gobierno Federal de Estados Unidos o un empleado de dicha entidad
            contratante que realiza compras a través de un contrato GSA Schedule
            en calidad de agente de dicha entidad, los términos de este Acuerdo
            publicado y la Política de Privacidad pueden diferir.
          </Typography>
          <Typography Element="h2" variant="h3">
            Cumplimiento de las normas de exportación y restricciones de uso
          </Typography>
          <Typography>
            El Usuario no exportará o reexportará, directa o indirectamente, los
            Servicios, ni cualquier información técnica relacionada con los
            mismos, a ningún destino o persona prohibida o restringida por la
            legislación aplicable, incluyendo, a título informativo y no
            limitativo, todas las leyes y los reglamentos de control de
            exportaciones de los Estados Unidos.
          </Typography>
          <Typography Element="h2" variant="h3">
            Ley aplicable, Foro, Arbitraje obligatorio y vinculante, Renuncia a
            acciones colectivas
          </Typography>
          <Typography>
            Cualquier acción relacionada con el presente Acuerdo, los Servicios
            y la relación del Usuario con Oura se regirá, se entenderá y se
            interpretará de acuerdo con las leyes del Estado de California, con
            independencia de sus principios de conflicto de leyes, y NO SE
            REGIRÁ ESPECÍFICAMENTE POR LAS CONVENCIONES DE LAS NACIONES UNIDAS
            SOBRE CONTRATOS DE COMPRAVENTA INTERNACIONAL DE MERCANCÍAS, SI SE
            APLICA DE OTRO MODO. El Usuario se compromete a resolver cualquier
            disputa o reclamación que surja o esté relacionada con el presente
            Acuerdo o con los Servicios mediante un arbitraje definitivo y
            vinculante por parte de un único árbitro. Esto incluye las disputas
            que surjan o estén relacionadas con la interpretación o aplicación
            de esta sección "Disposición de arbitraje obligatorio", incluyendo
            su aplicabilidad, revocabilidad o validez. Sin perjuicio de lo
            anterior, cualquiera de las partes podrá interponer una demanda
            judicial únicamente para impedir el uso no autorizado o el abuso de
            los Servicios, o la vulneración de cualquier propiedad intelectual.
            Sujeto a la Disposición de arbitraje obligatorio, las partes
            consienten irrevocablemente en presentar cualquier acción para
            resolver o hacer cumplir las reclamaciones que surjan bajo o en
            relación con el presente Acuerdo en los tribunales federales o
            estatales de San Francisco, California, y cada parte se somete
            irrevocablemente a la jurisdicción exclusiva de dichos tribunales en
            cualquier demanda, acción o procedimiento. Salvo en la medida en que
            lo prohíba la legislación aplicable, las Partes acuerdan que
            cualquier reclamación o causa de acción que surja de o esté
            relacionada con el uso de los Servicios o del presente Acuerdo
            deberá presentarse en el plazo de un (1) año desde el momento en que
            surgió dicha reclamación o causa de acción o prescribirá para
            siempre. Este párrafo no se aplica a los usuarios que residen en la
            Unión Europea. Si el Usuario reside en la Unión Europea, el presente
            Acuerdo se regirá por la legislación finlandesa y los tribunales
            finlandeses serán los únicos competentes para conocer los litigios
            relacionados con el mismo. Esta disposición no se aplicará a los
            consumidores de países que exigen que los acuerdos se rijan por las
            leyes locales del país del consumidor. El idioma inglés regirá todos
            los documentos, avisos e interpretaciones del presente Acuerdo. El
            Usuario también acepta renunciar a cualquier derecho a hacer valer
            cualquier reclamación contra Oura como representante o miembro en
            cualquier acción colectiva o representativa, excepto cuando dicha
            renuncia esté prohibida por la ley o sea considerada por un tribunal
            de justicia como contraria al orden público.
          </Typography>
          <Typography Element="h2" variant="h3">
            Temas diversos
          </Typography>
          <Typography>
            El Usuario reconoce que Oura tiene derecho a supervisar el uso de
            los Servicios para garantizar el cumplimiento del Acuerdo.
          </Typography>
          <Typography>
            Ninguna renuncia a cualquier término, disposición o condición del
            presente Acuerdo, ya sea por conducta o de otro modo, en uno o más
            casos, se considerará o constituirá una renuncia a cualquier otro
            término, disposición o condición del mismo, sea o no similar, ni
            constituirá una renuncia continua a dicho término, disposición o
            condición. Ninguna renuncia será vinculante a menos que sea
            ejecutada por escrito por la parte que hace la renuncia.
          </Typography>
          <Typography>
            El Usuario no puede ceder este Acuerdo a ninguna otra parte y
            cualquier intento de hacerlo será nulo.
          </Typography>
          <Typography>
            Si se determina que alguna de las disposiciones del presente Acuerdo
            es ilegal o inaplicable, dicha disposición se aplicará en la mayor
            medida posible, y las demás disposiciones seguirán siendo plenamente
            efectivas y aplicables.
          </Typography>
          <Typography>
            El presente Acuerdo y la Política de Privacidad constituyen la
            declaración completa y exclusiva del acuerdo entre el Usuario y Oura
            en relación con los Servicios, y sustituyen a todas y cada una de
            las comunicaciones, representaciones, declaraciones y entendimientos
            anteriores o contemporáneos, ya sean orales o escritos, entre las
            partes.
          </Typography>
          <Typography>
            En caso de conflicto entre los términos del presente Acuerdo y los
            términos de la Política de Privacidad, prevalecerán los términos del
            presente Acuerdo.
          </Typography>
          <Typography Element="h2" variant="h3">
            Modificación de las condiciones y servicios
          </Typography>
          <Typography>
            Oura se reserva el derecho de actualizar el presente Acuerdo y/o la
            Política de Privacidad en cualquier momento y por cualquier motivo a
            su entera discreción, publicando los términos actualizados. A menos
            que Oura indique lo contrario, cualquier cambio entrará en vigor de
            forma prospectiva a partir de la fecha de publicación. Oura
            notificará al Usuario cualquier cambio material en el Acuerdo o en
            los Servicios. El hecho de seguir accediendo o utilizando los
            Servicios una vez enviada la notificación de una modificación,
            implica que el Usuario acepta quedar vinculado por el Acuerdo
            modificado. Si el Acuerdo modificado no es aceptable para el
            Usuario, su único recurso es dejar de utilizar los Servicios. Oura y
            sus proveedores de servicios externos pueden realizar mejoras y/o
            cambios en los Servicios, las características y los precios
            descritos en cualquier momento y por cualquier motivo a su entera
            discreción. La Aplicación móvil puede descargar e instalar mejoras,
            actualizaciones y funciones adicionales con el fin de mejorar,
            potenciar y desarrollar aún más los Servicios. Oura se reserva el
            derecho de modificar o interrumpir en cualquier momento, de forma
            temporal o permanente, los Servicios o cualquier parte de los mismos
            con o sin previo aviso. El Usuario acepta que Oura no será
            responsable ante el Usuario ni ante ningún tercero por cualquier
            modificación, suspensión o interrupción de los Servicios.
          </Typography>
          <TermsOfDeliverySection />
          <Typography className="mt-40">
            {t('legal_disclaimer_translations')}
          </Typography>
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayout>
  );
};

Page.pageName = 'CONDICIONES DE USO';
Page.isSormusCompatible = true;

export default Page;
